import React from 'react';
import { DefaultButton, Modal, PrimaryButton, TextField, Stack } from '@fluentui/react';
import { apiClient } from '../Services/api-client';
import styles from './CreatePortfolioModal.module.css';

interface CreatePortfolioModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  onSaved: (portfolio: any, isEdit: boolean) => void;
  onError: (message: string) => void;
  existingPortfolio?: any;
}

export const CreatePortfolioModal: React.FC<CreatePortfolioModalProps> = ({
  isOpen,
  onDismiss,
  onSaved,
  onError,
  existingPortfolio,
}) => {
  const [loading, setLoading] = React.useState(false);
  const isEdit = Boolean(existingPortfolio?.portfolioId);
  const [formData, setFormData] = React.useState({
    friendlyName: '',
    apiHost: '',
    contact: { name: '', email: '', phone: '', address: '' },
  });

  React.useEffect(() => {
    if (!isOpen) {
      setFormData({
        friendlyName: '',
        apiHost: '',
        contact: { name: '', email: '', phone: '', address: '' },
      });
      return;
    }

    if (existingPortfolio) {
      setFormData({
        friendlyName: existingPortfolio.friendlyName || '',
        apiHost: existingPortfolio.apiHost || '',
        contact: {
          name: existingPortfolio.contact?.name || '',
          email: existingPortfolio.contact?.email || '',
          phone: existingPortfolio.contact?.phone || '',
          address: existingPortfolio.contact?.address || '',
        },
      });
    }
  }, [isOpen, existingPortfolio]);

  const isValid =
    Boolean(formData.friendlyName.trim()) &&
    Boolean(formData.apiHost.trim()) &&
    (formData.apiHost.startsWith('https://') || formData.apiHost.startsWith('http://')) &&
    Boolean(formData.contact.name.trim()) &&
    Boolean(formData.contact.email.trim()) &&
    Boolean(formData.contact.phone.trim()) &&
    Boolean(formData.contact.address.trim());

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!isValid) {
      onError('Complete all required fields with valid values.');
      return;
    }

    try {
      setLoading(true);
      const url = isEdit
        ? `/portfolios/${existingPortfolio.portfolioId}`
        : '/portfolios';
      const method = isEdit ? 'put' : 'post';
      const response = await apiClient[method](url, formData, {
        headers: { 'Content-Type': 'application/json' },
      });

      const created = response.data;
      onSaved(created, isEdit);
      onDismiss();
    } catch (error: any) {
      onError(error?.response?.data?.error || error?.message || 'Failed to save portfolio');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={onDismiss}
      isBlocking={false}
      containerClassName={styles.modal}
    >
      <div className={styles.header}>
        <h2>{isEdit ? 'Edit Portfolio' : 'Create Portfolio'}</h2>
        <p>{isEdit ? 'Update portfolio details and primary contact.' : 'Set up a new portfolio and primary contact.'}</p>
      </div>
      <form onSubmit={handleSubmit}>
        <TextField
          label="Portfolio Name *"
          value={formData.friendlyName}
          onChange={(e, value) =>
            setFormData((prev) => ({ ...prev, friendlyName: value || '' }))
          }
          placeholder="e.g., Kaanha Properties"
          styles={{ root: { marginBottom: '18px' } }}
        />
        <TextField
          label="API Host (HTTP or HTTPS) *"
          value={formData.apiHost}
          onChange={(e, value) =>
            setFormData((prev) => ({ ...prev, apiHost: value || '' }))
          }
          placeholder="https://api.example.com"
          styles={{ root: { marginBottom: '18px' } }}
        />
        <div className={styles.sectionTitle}>Primary Contact</div>
        <Stack horizontal tokens={{ childrenGap: 16 }}>
          <TextField
            label="Name *"
            value={formData.contact.name}
            onChange={(e, value) =>
              setFormData((prev) => ({
                ...prev,
                contact: { ...prev.contact, name: value || '' },
              }))
            }
            styles={{ root: { flex: 1, marginBottom: '18px' } }}
          />
          <TextField
            label="Email *"
            value={formData.contact.email}
            onChange={(e, value) =>
              setFormData((prev) => ({
                ...prev,
                contact: { ...prev.contact, email: value || '' },
              }))
            }
            type="email"
            styles={{ root: { flex: 1, marginBottom: '18px' } }}
          />
        </Stack>
        <Stack horizontal tokens={{ childrenGap: 16 }}>
          <TextField
            label="Phone *"
            value={formData.contact.phone}
            onChange={(e, value) =>
              setFormData((prev) => ({
                ...prev,
                contact: { ...prev.contact, phone: value || '' },
              }))
            }
            styles={{ root: { flex: 1, marginBottom: '18px' } }}
          />
          <TextField
            label="Address *"
            value={formData.contact.address}
            onChange={(e, value) =>
              setFormData((prev) => ({
                ...prev,
                contact: { ...prev.contact, address: value || '' },
              }))
            }
            styles={{ root: { flex: 1, marginBottom: '18px' } }}
          />
        </Stack>
        <div className={styles.actions}>
          <PrimaryButton
            type="submit"
            text={loading ? (isEdit ? 'Saving...' : 'Creating...') : (isEdit ? 'Save' : 'Create')}
            disabled={!isValid || loading}
          />
          <DefaultButton text="Cancel" onClick={onDismiss} disabled={loading} />
        </div>
      </form>
    </Modal>
  );
};
